import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '../components/ui/collapsible';
import { API } from '../lib/utils';
import { DOMAINS } from '../config/serviceCategories';
import { toast } from 'sonner';
import { 
  ChevronLeft, ChevronDown, Save, Upload, Building2, 
  Phone, Mail, MapPin, FileText, LogOut,
  Landmark, Shield, Award, AlertTriangle, Info, Briefcase
} from 'lucide-react';

const FORMES_JURIDIQUES = [
  { value: 'auto-entrepreneur', label: 'Auto-entrepreneur' },
  { value: 'ei', label: 'EI (Entreprise Individuelle)' },
  { value: 'eirl', label: 'EIRL' },
  { value: 'sarl', label: 'SARL' },
  { value: 'sas', label: 'SAS' },
  { value: 'sasu', label: 'SASU' },
  { value: 'eurl', label: 'EURL' },
  { value: 'autre', label: 'Autre' }
];

const BATIMENT_DOMAINS = ['electricite', 'plomberie', 'maconnerie', 'peinture', 'menuiserie', 'couverture', 'serrurerie', 'carrelage', 'plaquiste', 'facade', 'chauffage_clim', 'terrassement', 'vitrerie', 'cuisine_sdb', 'multiservices'];
const ENTRETIEN_DOMAINS = ['jardinage', 'nettoyage', 'aide_personne', 'animaux'];

const DELAIS_PAIEMENT = [
  { value: 'reception', label: 'À réception' },
  { value: '30', label: '30 jours' },
  { value: '45', label: '45 jours' },
  { value: '60', label: '60 jours' }
];

const ProfilePage = () => {
  const navigate = useNavigate();
  const { company, updateCompany, user, logout, canWrite } = useAuth();
  const fileInputRef = useRef(null);
  const [saving, setSaving] = useState(false);
  
  // Collapsible states
  const [openSections, setOpenSections] = useState({
    entreprise: true,
    activite: true,
    tva: true,
    decennale: true,
    rcpro: true,
    qualifications: true,
    banque: true,
    mentions: true
  });

  const [formData, setFormData] = useState({
    name: '',
    logo: '',
    siret: '',
    address: '',
    city: '',
    postal_code: '',
    phone: '',
    email: '',
    tva_number: '',
    bank_name: '',
    iban: '',
    bic: '',
    is_auto_entrepreneur: false,
    escompte_text: '',
    // New fields
    forme_juridique: '',
    secteur_activite: '',
    rcs_number: '',
    rcs_city: '',
    rm_number: '',
    tva_regime: 'franchise',
    has_decennale: false,
    assurance_decennale_name: '',
    assurance_decennale_number: '',
    assurance_decennale_coverage: '',
    has_rc_pro: false,
    rc_pro_name: '',
    rc_pro_number: '',
    qualifications: '',
    default_payment_delay: 'reception',
    capital_social: ''
  });

  // Fetch fresh company data on mount
  useEffect(() => {
    const fetchCompanyData = async () => {
      try {
        const response = await axios.get(`${API}/company`);
        if (response.data) {
          updateCompany(response.data);
        }
      } catch (error) {
        console.log('No company profile found');
      }
    };
    fetchCompanyData();
  }, [updateCompany]);

  // Sync form data when company or user loads
  useEffect(() => {
    setFormData({
      name: company?.name || '',
      logo: company?.logo || '',
      siret: company?.siret || '',
      address: company?.address || '',
      city: company?.city || '',
      postal_code: company?.postal_code || '',
      phone: company?.phone || '',
      email: company?.email || user?.email || '',
      tva_number: company?.tva_number || '',
      bank_name: company?.bank_name || '',
      iban: company?.iban || '',
      bic: company?.bic || '',
      is_auto_entrepreneur: company?.is_auto_entrepreneur || false,
      escompte_text: company?.escompte_text || '',
      // New fields
      forme_juridique: company?.forme_juridique || '',
      secteur_activite: company?.secteur_activite || '',
      rcs_number: company?.rcs_number || '',
      rcs_city: company?.rcs_city || '',
      rm_number: company?.rm_number || '',
      tva_regime: company?.tva_regime || 'franchise',
      has_decennale: company?.has_decennale || false,
      assurance_decennale_name: company?.assurance_decennale_name || '',
      assurance_decennale_number: company?.assurance_decennale_number || '',
      assurance_decennale_coverage: company?.assurance_decennale_coverage || '',
      has_rc_pro: company?.has_rc_pro || false,
      rc_pro_name: company?.rc_pro_name || '',
      rc_pro_number: company?.rc_pro_number || '',
      qualifications: company?.qualifications || '',
      default_payment_delay: company?.default_payment_delay || 'reception',
      capital_social: company?.capital_social || ''
    });
  }, [company, user]);

  // Computed conditions
  const showRCS = ['sarl', 'sas', 'sasu', 'eurl'].includes(formData.forme_juridique);
  const showRM = ['auto-entrepreneur', 'ei', 'eirl'].includes(formData.forme_juridique);
  const showCapitalSocial = ['sarl', 'sas', 'sa', 'sasu'].includes(formData.forme_juridique);
  const showDecennale = BATIMENT_DOMAINS.includes(formData.secteur_activite);
  const showRCPro = [...BATIMENT_DOMAINS, ...ENTRETIEN_DOMAINS].includes(formData.secteur_activite);
  const showQualifications = [...BATIMENT_DOMAINS, ...ENTRETIEN_DOMAINS].includes(formData.secteur_activite);
  const isFranchise = formData.tva_regime === 'franchise';

  const toggleSection = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > 2 * 1024 * 1024) {
      toast.error('Le fichier est trop volumineux (max 2MB)');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      setFormData({ ...formData, logo: event.target?.result });
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Guard readonly
    if (!canWrite()) {
      navigate('/subscription');
      return;
    }
    
    // Validate required fields
    const requiredFields = {
      name: 'Nom de l\'entreprise',
      siret: 'SIRET',
      address: 'Adresse',
      city: 'Ville',
      postal_code: 'Code postal',
      phone: 'Téléphone',
      email: 'Email',
      forme_juridique: 'Forme juridique',
      secteur_activite: 'Secteur d\'activité'
    };
    
    const missingFields = Object.entries(requiredFields)
      .filter(([key]) => !formData[key] || formData[key].trim() === '')
      .map(([, label]) => label);
    
    if (missingFields.length > 0) {
      toast.error(`Champs obligatoires manquants: ${missingFields.join(', ')}`);
      return;
    }

    // Validate TVA number if regime is normale
    if (formData.tva_regime === 'normale' && !formData.tva_number?.trim()) {
      toast.error('Le numéro de TVA est obligatoire en régime de TVA normale');
      return;
    }

    // Validate decennale if batiment
    if (showDecennale && formData.has_decennale) {
      if (!formData.assurance_decennale_name || !formData.assurance_decennale_number || !formData.assurance_decennale_coverage) {
        toast.error('Veuillez renseigner toutes les informations de l\'assurance décennale');
        return;
      }
    }

    setSaving(true);
    try {
      const payload = {
        name: formData.name.trim(),
        siret: formData.siret.trim(),
        address: formData.address.trim(),
        city: formData.city.trim(),
        postal_code: formData.postal_code.trim(),
        phone: formData.phone.trim(),
        email: formData.email.trim(),
        tva_number: formData.tva_number?.trim() || null,
        logo: formData.logo || null,
        bank_name: formData.bank_name?.trim() || null,
        iban: formData.iban?.trim() || null,
        bic: formData.bic?.trim() || null,
        is_auto_entrepreneur: formData.forme_juridique === 'auto-entrepreneur',
        escompte_text: formData.escompte_text?.trim() || null,
        // New fields
        forme_juridique: formData.forme_juridique || null,
        secteur_activite: formData.secteur_activite || null,
        rcs_number: showRCS ? formData.rcs_number?.trim() || null : null,
        rcs_city: showRCS ? formData.rcs_city?.trim() || null : null,
        rm_number: showRM ? formData.rm_number?.trim() || null : null,
        tva_regime: formData.tva_regime,
        has_decennale: showDecennale ? formData.has_decennale : false,
        assurance_decennale_name: showDecennale && formData.has_decennale ? formData.assurance_decennale_name?.trim() || null : null,
        assurance_decennale_number: showDecennale && formData.has_decennale ? formData.assurance_decennale_number?.trim() || null : null,
        assurance_decennale_coverage: showDecennale && formData.has_decennale ? formData.assurance_decennale_coverage?.trim() || null : null,
        has_rc_pro: showRCPro ? formData.has_rc_pro : false,
        rc_pro_name: showRCPro && formData.has_rc_pro ? formData.rc_pro_name?.trim() || null : null,
        rc_pro_number: showRCPro && formData.has_rc_pro ? formData.rc_pro_number?.trim() || null : null,
        qualifications: showQualifications ? formData.qualifications?.trim() || null : null,
        default_payment_delay: formData.default_payment_delay,
        capital_social: showCapitalSocial ? formData.capital_social?.trim() || null : null
      };
      
      const response = await axios.post(`${API}/company`, payload);
      updateCompany(response.data);
      toast.success('Profil entreprise enregistré');
    } catch (error) {
      console.error('Profile save error:', error);
      const errorMsg = error.response?.data?.detail;
      if (typeof errorMsg === 'object' && errorMsg?.msg) {
        toast.error(`Erreur: ${errorMsg.msg}`);
      } else if (typeof errorMsg === 'string') {
        toast.error(errorMsg);
      } else {
        toast.error('Erreur lors de la sauvegarde. Vérifiez votre connexion.');
      }
    }
    setSaving(false);
  };

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const SectionHeader = ({ icon: Icon, title, section, color = "text-primary" }) => (
    <CollapsibleTrigger className="flex items-center justify-between w-full p-4 hover:bg-muted/30 rounded-t-lg transition-colors">
      <div className="flex items-center gap-2">
        <Icon className={`w-5 h-5 ${color}`} />
        <span className="text-lg font-semibold">{title}</span>
      </div>
      <ChevronDown className={`w-5 h-5 transition-transform ${openSections[section] ? 'rotate-180' : ''}`} />
    </CollapsibleTrigger>
  );

  return (
    <div className="page-container p-4 md:p-8 noise-overlay">
      <div className="relative z-10 max-w-2xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button 
            variant="ghost" 
            size="icon"
            onClick={() => navigate('/')}
            className="h-10 w-10"
            data-testid="back-btn"
          >
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <h1 className="text-3xl md:text-4xl font-bold tracking-tight uppercase">Profil</h1>
        </div>

        {/* User Info */}
        <Card className="bg-card border-white/10 mb-6">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="font-semibold">{user?.full_name}</p>
              <p className="text-sm text-muted-foreground">{user?.email}</p>
            </div>
            <Button 
              variant="outline" 
              onClick={handleLogout}
              className="border-white/10"
              data-testid="logout-btn"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Déconnexion
            </Button>
          </CardContent>
        </Card>

        {/* Company Form */}
        <form onSubmit={handleSubmit}>
          {/* Section: Informations entreprise */}
          <Card className="bg-card border-white/10 mb-4">
            <Collapsible open={openSections.entreprise} onOpenChange={() => toggleSection('entreprise')}>
              <SectionHeader icon={Building2} title="Informations entreprise" section="entreprise" />
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
                  {/* Logo */}
                  <div className="flex items-center gap-4">
                    <div 
                      className="logo-upload"
                      onClick={() => fileInputRef.current?.click()}
                      data-testid="logo-upload"
                    >
                      {formData.logo ? (
                        <img src={formData.logo} alt="Logo" />
                      ) : (
                        <div className="text-center text-muted-foreground">
                          <Upload className="w-8 h-8 mx-auto mb-2" />
                          <span className="text-xs">Logo</span>
                        </div>
                      )}
                    </div>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                    <div className="flex-1">
                      <p className="text-sm font-medium">Logo de l'entreprise</p>
                      <p className="text-xs text-muted-foreground">PNG, JPG jusqu'à 2MB</p>
                      {formData.logo && (
                        <Button 
                          type="button"
                          variant="link" 
                          className="p-0 h-auto text-destructive text-xs"
                          onClick={() => setFormData({ ...formData, logo: '' })}
                        >
                          Supprimer
                        </Button>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="forme_juridique">Forme juridique *</Label>
                    <Select 
                      value={formData.forme_juridique || undefined} 
                      onValueChange={(v) => setFormData({ ...formData, forme_juridique: v })}
                    >
                      <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="forme-juridique-select">
                        <SelectValue placeholder="Sélectionnez..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10">
                        {FORMES_JURIDIQUES.map(f => (
                          <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="name">Nom de l'entreprise *</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent focus:border-primary"
                      placeholder="Plomberie Dupont"
                      data-testid="company-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="siret">SIRET *</Label>
                    <Input
                      id="siret"
                      value={formData.siret}
                      onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent focus:border-primary"
                      placeholder="123 456 789 00012"
                      data-testid="company-siret-input"
                    />
                  </div>

                  {showRCS && (
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="rcs_number">Numéro RCS</Label>
                        <Input
                          id="rcs_number"
                          value={formData.rcs_number}
                          onChange={(e) => setFormData({ ...formData, rcs_number: e.target.value })}
                          className="h-12 bg-muted/50 border-transparent focus:border-primary"
                          placeholder="123 456 789"
                          data-testid="rcs-number-input"
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="rcs_city">Ville RCS</Label>
                        <Input
                          id="rcs_city"
                          value={formData.rcs_city}
                          onChange={(e) => setFormData({ ...formData, rcs_city: e.target.value })}
                          className="h-12 bg-muted/50 border-transparent focus:border-primary"
                          placeholder="Paris"
                          data-testid="rcs-city-input"
                        />
                      </div>
                    </div>
                  )}

                  {showCapitalSocial && (
                    <div className="space-y-2">
                      <Label htmlFor="capital_social">Capital social (€)</Label>
                      <Input
                        id="capital_social"
                        value={formData.capital_social}
                        onChange={(e) => setFormData({ ...formData, capital_social: e.target.value })}
                        className="h-12 bg-muted/50 border-transparent focus:border-primary"
                        placeholder="Ex: 10 000"
                        data-testid="capital-social-input"
                      />
                    </div>
                  )}

                  {showRM && (
                    <div className="space-y-2">
                      <Label htmlFor="rm_number">Numéro RM (Répertoire des Métiers)</Label>
                      <Input
                        id="rm_number"
                        value={formData.rm_number}
                        onChange={(e) => setFormData({ ...formData, rm_number: e.target.value })}
                        className="h-12 bg-muted/50 border-transparent focus:border-primary"
                        placeholder="Ex: 31 RM 12345"
                        data-testid="rm-number-input"
                      />
                    </div>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="address">Adresse *</Label>
                    <Input
                      id="address"
                      value={formData.address}
                      onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent focus:border-primary"
                      placeholder="123 Rue de la Plomberie"
                      data-testid="company-address-input"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="postal_code">Code postal *</Label>
                      <Input
                        id="postal_code"
                        value={formData.postal_code}
                        onChange={(e) => setFormData({ ...formData, postal_code: e.target.value })}
                        className="h-12 bg-muted/50 border-transparent focus:border-primary"
                        placeholder="75001"
                        data-testid="company-postal-input"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="city">Ville *</Label>
                      <Input
                        id="city"
                        value={formData.city}
                        onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                        className="h-12 bg-muted/50 border-transparent focus:border-primary"
                        placeholder="Paris"
                        data-testid="company-city-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="phone">Téléphone *</Label>
                    <Input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent focus:border-primary"
                      placeholder="06 12 34 56 78"
                      data-testid="company-phone-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="company_email">Email *</Label>
                    <Input
                      id="company_email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent focus:border-primary"
                      placeholder="contact@plomberie-dupont.fr"
                      data-testid="company-email-input"
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Section: Votre activité */}
          <Card className="bg-card border-white/10 mb-4">
            <Collapsible open={openSections.activite} onOpenChange={() => toggleSection('activite')}>
              <SectionHeader icon={Briefcase} title="Votre activité" section="activite" />
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
                  <div className="space-y-2">
                    <Label htmlFor="secteur_activite">Secteur d'activité *</Label>
                    <Select 
                      value={formData.secteur_activite} 
                      onValueChange={(v) => setFormData({ ...formData, secteur_activite: v })}
                    >
                      <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="secteur-activite-select">
                        <SelectValue placeholder="Sélectionnez votre métier..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10 max-h-[300px]">
                        {DOMAINS.filter(d => !d.isCustom).map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Section: TVA */}
          <Card className="bg-card border-white/10 mb-4">
            <Collapsible open={openSections.tva} onOpenChange={() => toggleSection('tva')}>
              <SectionHeader icon={FileText} title="TVA" section="tva" />
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
                  <div className="space-y-2">
                    <Label htmlFor="tva_regime">Régime de TVA *</Label>
                    <Select 
                      value={formData.tva_regime || undefined} 
                      onValueChange={(v) => setFormData({ ...formData, tva_regime: v })}
                    >
                      <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="tva-regime-select">
                        <SelectValue placeholder="Sélectionnez..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10">
                        <SelectItem value="franchise">Franchise en base (je ne facture pas la TVA)</SelectItem>
                        <SelectItem value="normale">TVA normale (je facture la TVA)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {isFranchise ? (
                    <div className="flex items-start gap-3 p-3 bg-primary/10 border border-primary/30 rounded-lg">
                      <Info className="w-5 h-5 text-primary flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-primary">
                        La mention "TVA non applicable, art. 293 B du CGI" sera ajoutée automatiquement sur vos devis et factures.
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <Label htmlFor="tva_number">N° TVA Intracommunautaire *</Label>
                      <Input
                        id="tva_number"
                        value={formData.tva_number}
                        onChange={(e) => setFormData({ ...formData, tva_number: e.target.value })}
                        className="h-12 bg-muted/50 border-transparent focus:border-primary"
                        placeholder="FR12345678901"
                        data-testid="company-tva-input"
                      />
                    </div>
                  )}
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Section: Assurance décennale */}
          {showDecennale && (
            <Card className="bg-card border-white/10 mb-4">
              <Collapsible open={openSections.decennale} onOpenChange={() => toggleSection('decennale')}>
                <SectionHeader icon={Shield} title="Assurance décennale" section="decennale" color="text-blue-400" />
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <AlertTriangle className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                      <p className="text-sm text-blue-400">
                        Obligatoire pour les artisans du bâtiment. Ces informations apparaîtront sur vos documents.
                      </p>
                    </div>

                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <Label htmlFor="has_decennale" className="font-medium">J'ai une assurance décennale</Label>
                      <Switch
                        id="has_decennale"
                        checked={formData.has_decennale}
                        onCheckedChange={(checked) => setFormData({ ...formData, has_decennale: checked })}
                        data-testid="has-decennale-switch"
                      />
                    </div>

                    {formData.has_decennale && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="assurance_decennale_name">Nom de l'assureur *</Label>
                          <Input
                            id="assurance_decennale_name"
                            value={formData.assurance_decennale_name}
                            onChange={(e) => setFormData({ ...formData, assurance_decennale_name: e.target.value })}
                            className="h-12 bg-muted/50 border-transparent focus:border-primary"
                            placeholder="Ex: AXA, MAAF, MMA"
                            data-testid="decennale-name-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="assurance_decennale_number">Numéro de police *</Label>
                          <Input
                            id="assurance_decennale_number"
                            value={formData.assurance_decennale_number}
                            onChange={(e) => setFormData({ ...formData, assurance_decennale_number: e.target.value })}
                            className="h-12 bg-muted/50 border-transparent focus:border-primary"
                            placeholder="Ex: 1234567890"
                            data-testid="decennale-number-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="assurance_decennale_coverage">Couverture géographique *</Label>
                          <Input
                            id="assurance_decennale_coverage"
                            value={formData.assurance_decennale_coverage}
                            onChange={(e) => setFormData({ ...formData, assurance_decennale_coverage: e.target.value })}
                            className="h-12 bg-muted/50 border-transparent focus:border-primary"
                            placeholder="Ex: France entière"
                            data-testid="decennale-coverage-input"
                          />
                        </div>
                      </>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )}

          {/* Section: RC Pro */}
          {showRCPro && (
            <Card className="bg-card border-white/10 mb-4">
              <Collapsible open={openSections.rcpro} onOpenChange={() => toggleSection('rcpro')}>
                <SectionHeader icon={Shield} title="Assurance RC Professionnelle" section="rcpro" />
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    {formData.secteur_activite === 'entretien' && (
                      <div className="flex items-start gap-3 p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                        <Info className="w-5 h-5 text-blue-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-blue-400">
                          Recommandée mais non obligatoire pour votre activité.
                        </p>
                      </div>
                    )}

                    <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                      <Label htmlFor="has_rc_pro" className="font-medium">J'ai une assurance RC Professionnelle</Label>
                      <Switch
                        id="has_rc_pro"
                        checked={formData.has_rc_pro}
                        onCheckedChange={(checked) => setFormData({ ...formData, has_rc_pro: checked })}
                        data-testid="has-rc-pro-switch"
                      />
                    </div>

                    {formData.has_rc_pro && (
                      <>
                        <div className="space-y-2">
                          <Label htmlFor="rc_pro_name">Nom de l'assureur</Label>
                          <Input
                            id="rc_pro_name"
                            value={formData.rc_pro_name}
                            onChange={(e) => setFormData({ ...formData, rc_pro_name: e.target.value })}
                            className="h-12 bg-muted/50 border-transparent focus:border-primary"
                            placeholder="Ex: AXA, MAAF"
                            data-testid="rc-pro-name-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="rc_pro_number">Numéro de police</Label>
                          <Input
                            id="rc_pro_number"
                            value={formData.rc_pro_number}
                            onChange={(e) => setFormData({ ...formData, rc_pro_number: e.target.value })}
                            className="h-12 bg-muted/50 border-transparent focus:border-primary"
                            placeholder="Ex: 1234567890"
                            data-testid="rc-pro-number-input"
                          />
                        </div>
                      </>
                    )}
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )}

          {/* Section: Qualifications */}
          {showQualifications && (
            <Card className="bg-card border-white/10 mb-4">
              <Collapsible open={openSections.qualifications} onOpenChange={() => toggleSection('qualifications')}>
                <SectionHeader icon={Award} title="Qualifications" section="qualifications" />
                <CollapsibleContent>
                  <CardContent className="space-y-4 pt-0">
                    <div className="space-y-2">
                      <Label htmlFor="qualifications">Qualifications / Certifications</Label>
                      <Textarea
                        id="qualifications"
                        value={formData.qualifications}
                        onChange={(e) => setFormData({ ...formData, qualifications: e.target.value })}
                        className="bg-muted/50 border-transparent focus:border-primary resize-none"
                        placeholder={formData.secteur_activite === 'batiment' 
                          ? "Ex: RGE, Qualibat, QualiPV, QualiPAC..." 
                          : "Ex: Certifié, agréé service à la personne..."}
                        rows={2}
                        data-testid="qualifications-input"
                      />
                    </div>
                  </CardContent>
                </CollapsibleContent>
              </Collapsible>
            </Card>
          )}

          {/* Section: Coordonnées bancaires */}
          <Card className="bg-card border-white/10 mb-4">
            <Collapsible open={openSections.banque} onOpenChange={() => toggleSection('banque')}>
              <SectionHeader icon={Landmark} title="Coordonnées bancaires" section="banque" />
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
                  <p className="text-xs text-muted-foreground">
                    Ces informations apparaîtront sur vos factures pour le règlement par virement.
                  </p>
                  
                  <div className="space-y-2">
                    <Label htmlFor="bank_name">Nom de la banque</Label>
                    <Input
                      id="bank_name"
                      value={formData.bank_name}
                      onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent focus:border-primary"
                      placeholder="Crédit Agricole"
                      data-testid="company-bank-name-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="iban">IBAN</Label>
                    <Input
                      id="iban"
                      value={formData.iban}
                      onChange={(e) => setFormData({ ...formData, iban: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent focus:border-primary font-mono"
                      placeholder="FR76 1234 5678 9012 3456 7890 123"
                      data-testid="company-iban-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="bic">BIC/SWIFT</Label>
                    <Input
                      id="bic"
                      value={formData.bic}
                      onChange={(e) => setFormData({ ...formData, bic: e.target.value })}
                      className="h-12 bg-muted/50 border-transparent focus:border-primary font-mono"
                      placeholder="AGRIFRPP"
                      data-testid="company-bic-input"
                    />
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          {/* Section: Mentions légales */}
          <Card className="bg-card border-white/10 mb-6">
            <Collapsible open={openSections.mentions} onOpenChange={() => toggleSection('mentions')}>
              <SectionHeader icon={FileText} title="Mentions légales" section="mentions" />
              <CollapsibleContent>
                <CardContent className="space-y-4 pt-0">
                  <div className="space-y-2">
                    <Label htmlFor="secteur_activite">Votre métier principal *</Label>
                    <Select 
                      value={formData.secteur_activite} 
                      onValueChange={(v) => setFormData({ ...formData, secteur_activite: v })}
                    >
                      <SelectTrigger className="h-12 bg-muted/50 border-transparent" data-testid="secteur-activite-select">
                        <SelectValue placeholder="Sélectionnez votre métier..." />
                      </SelectTrigger>
                      <SelectContent className="bg-card border-white/10 max-h-[300px]">
                        {DOMAINS.filter(d => !d.isCustom).map(d => (
                          <SelectItem key={d.value} value={d.value}>{d.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="escompte_text">Escompte pour paiement anticipé</Label>
                    <Textarea
                      id="escompte_text"
                      value={formData.escompte_text}
                      onChange={(e) => setFormData({ ...formData, escompte_text: e.target.value })}
                      className="bg-muted/50 border-transparent focus:border-primary resize-none"
                      placeholder="Escompte de 2% pour paiement sous 10 jours"
                      rows={2}
                      data-testid="company-escompte-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Laissez vide pour afficher "Pas d'escompte accordé pour paiement anticipé"
                    </p>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Collapsible>
          </Card>

          <Button
            type="submit"
            disabled={saving}
            className="w-full h-12 bg-primary text-primary-foreground hover:bg-accent font-bold uppercase tracking-wide rounded-full"
            data-testid="save-profile-btn"
          >
            <Save className="w-5 h-5 mr-2" />
            {saving ? 'Enregistrement...' : 'Enregistrer'}
          </Button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
